import { useCallback, useEffect, useMemo, useState } from "react";
import { apiUrl } from "utils/apiUrl";
import { clientSafeMessage } from "utils/clientSafeMessage";
import { readClientSession } from "utils/clientSession";

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function StatusBadge({ status }) {
  const verified = String(status || "").toLowerCase() === "verified";
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-bold ${
        verified
          ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-200"
          : "bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200"
      }`}
    >
      {verified ? "Verified" : "Not Verified"}
    </span>
  );
}

export default function ClientReferral() {
  const session = readClientSession();
  const clientId =
    session?.clientId != null ? String(session.clientId) : "";

  const [referralLink, setReferralLink] = useState("");
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const fallbackLink = useMemo(() => {
    if (!clientId) return "";
    const origin =
      typeof window !== "undefined" ? window.location.origin : "https://alphasniper.io";
    return `${origin}/SignUp?ref=${encodeURIComponent(clientId)}`;
  }, [clientId]);

  const load = useCallback(async () => {
    if (!clientId) {
      setLoading(false);
      setError("Sign in to view your referral link.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await fetch(apiUrl("/api/client/referral"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId: Number(clientId) }),
        cache: "no-store",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(clientSafeMessage(data.message || "Could not load referral data."));
      }
      setReferralLink(String(data.referralLink || fallbackLink));
      setReferrals(Array.isArray(data.referrals) ? data.referrals : []);
    } catch {
      setReferralLink(fallbackLink);
      setReferrals([]);
      setError("");
    } finally {
      setLoading(false);
    }
  }, [clientId, fallbackLink]);

  useEffect(() => {
    void load();
  }, [load]);

  async function copyLink() {
    const link = referralLink || fallbackLink;
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  const displayLink = referralLink || fallbackLink;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-navy-700 dark:text-white">Referral</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Share your link. Each validated referral counts toward the contest in your country.
        </p>
      </div>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-navy-800 sm:p-6">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Your referral link
        </h2>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
          <code className="block flex-1 break-all rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-navy-800 dark:border-white/10 dark:bg-navy-900 dark:text-gray-100">
            {displayLink || "—"}
          </code>
          <button
            type="button"
            onClick={() => void copyLink()}
            disabled={!displayLink}
            className="shrink-0 rounded-xl bg-brand-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-navy-800">
        <div className="border-b border-gray-100 px-5 py-4 dark:border-white/10 sm:px-6">
          <h2 className="text-lg font-bold text-navy-700 dark:text-white">Referred clients</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            ClientID · verification status · first subscription date
          </p>
        </div>

        {loading ? (
          <p className="px-5 py-10 text-center text-sm text-gray-500 dark:text-gray-400 sm:px-6">
            Loading referrals…
          </p>
        ) : error ? (
          <p className="px-5 py-10 text-center text-sm text-red-600 dark:text-red-400 sm:px-6">
            {error}
          </p>
        ) : referrals.length === 0 ? (
          <p className="px-5 py-10 text-center text-sm text-gray-500 dark:text-gray-400 sm:px-6">
            No referred clients yet. Share your link to get started.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-left text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80 text-xs uppercase tracking-wide text-gray-500 dark:border-white/10 dark:bg-navy-900/50 dark:text-gray-400">
                  <th className="px-5 py-3 font-semibold sm:px-6">ClientID</th>
                  <th className="px-5 py-3 font-semibold sm:px-6">Status</th>
                  <th className="px-5 py-3 font-semibold sm:px-6">Subscription date</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((row) => (
                  <tr
                    key={row.clientId}
                    className="border-b border-gray-50 last:border-0 dark:border-white/5"
                  >
                    <td className="px-5 py-3.5 font-semibold tabular-nums text-navy-800 dark:text-white sm:px-6">
                      {row.clientId}
                    </td>
                    <td className="px-5 py-3.5 sm:px-6">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="px-5 py-3.5 tabular-nums text-gray-600 dark:text-gray-300 sm:px-6">
                      {formatDate(row.subscriptionDate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
